/**
 * Build writingData (user's writing texts) from exam structure and userAnswers.
 * Used so we always persist the user's writing on the exam result, even when no correction runs.
 * @param {{ sections: Array<{ parts: Array<{ partId: string, partName?: string, taskType?: string }> }> }} examData
 * @param {Record<string, unknown>} userAnswers
 * @returns {{ hasMultipleTexts: boolean, texts: Record<string, { text: string, partId: string, partName: string, [key: string]: unknown }>, parts: unknown[] } | null}
 */
export function buildWritingDataFromExam(examData, userAnswers) {
  if (!examData?.sections || !userAnswers || typeof userAnswers !== 'object') {
    return null;
  }

  const writingTaskTypes = [
    'writing', 'choice-writing-essay', 'email', 'email-formal', 'email-informal',
    'forum-post', 'formal-response-email', 'formal-complaint', 'formal-message',
    'formal-letter', 'informal-letter', 'essay', 'argumentative-essay',
    'email-opinion', 'choice-writing'
  ];

  const allWritingTexts = {};
  const writingPartsFound = [];
  const provider = (examData.examProvider || '').toString();

  for (const section of examData.sections) {
    if (!section.parts) continue;
    for (const part of section.parts) {
      if (!writingTaskTypes.includes(part.taskType)) continue;

      const partAnswers = userAnswers[part.partId];
      if (!partAnswers || typeof partAnswers !== 'object') continue;

      if (partAnswers.hasMultipleTasks && partAnswers.allTaskTexts && typeof partAnswers.allTaskTexts === 'object') {
        for (const [taskIndex, taskData] of Object.entries(partAnswers.allTaskTexts)) {
          if (taskData && typeof taskData === 'object' && taskData.text && String(taskData.text).trim()) {
            const key = `${part.partId}_task${taskIndex}`;
            allWritingTexts[key] = {
              text: String(taskData.text).trim(),
              partId: part.partId,
              partName: part.partName || `Aufgabe ${Object.keys(allWritingTexts).length + 1}`,
              taskType: part.taskType,
              taskIndex: parseInt(taskIndex, 10),
              wordCount: (taskData.wordCount != null) ? taskData.wordCount : String(taskData.text).trim().split(/\s+/).filter(Boolean).length,
              metadata: taskData,
              taskData: (taskData.taskData != null) ? taskData.taskData : null
            };
            writingPartsFound.push(part);
          }
        }
      } else {
        const possibleKeys = [
          `${provider}_${part.partId}_writingText`,
          `${provider}_${part.partId}_text`,
          'writingText',
          'text',
          'task0_text',
          'A_text',
          'B_text'
        ];

        let foundText = null;
        for (const key of possibleKeys) {
          const val = partAnswers[key];
          if (val != null && typeof val === 'string' && val.trim()) {
            foundText = val.trim();
            break;
          }
        }

        if (foundText) {
          const taskDataKey = `${part.partId}_taskData`;
          const taskData = partAnswers[taskDataKey] || null;
          allWritingTexts[part.partId] = {
            text: foundText,
            partId: part.partId,
            partName: part.partName || `Aufgabe ${Object.keys(allWritingTexts).length + 1}`,
            taskType: part.taskType || 'writing',
            wordCount: foundText.split(/\s+/).filter(Boolean).length,
            taskData: taskData
          };
          writingPartsFound.push(part);
        }
      }
    }
  }

  if (Object.keys(allWritingTexts).length === 0) {
    return null;
  }

  return {
    hasMultipleTexts: Object.keys(allWritingTexts).length > 1,
    texts: allWritingTexts,
    parts: writingPartsFound
  };
}
